import React, { useRef } from 'react';

interface SidebarPhotoCardProps {
  url?: string;
  alt: string;
  uploading?: boolean;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  title?: string;
  /** Renders the preview at a 1:1 aspect ratio (`.sidebar-photo-preview--square`). */
  square?: boolean;
  /** Optional drag-and-drop support — wire both together to accept a dropped file. */
  onDragOver?: (e: React.DragEvent<HTMLElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLElement>) => void;
}

// Single-photo sidebar card — preview with change/remove icons, or an
// upload button when empty. Shared shape across GuestForm, FabricCatalog,
// FabricFormPage, etc, which previously hand-rolled this markup separately.
export function SidebarPhotoCard({ url, alt, uploading, onFileSelect, onRemove, title = 'Photo', square, onDragOver, onDrop }: SidebarPhotoCardProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <section className="side-section">
      <h4>{title}</h4>
      {url ? (
        <div
          className={`sidebar-photo-preview${square ? ' sidebar-photo-preview--square' : ''}`}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          <img src={url} alt={alt} />
          <button
            type="button"
            className="sidebar-photo-edit-icon"
            onClick={() => inputRef.current?.click()}
            title="Change photo"
          >
            ✎
          </button>
          <button
            type="button"
            className="sidebar-photo-remove-icon"
            onClick={onRemove}
            title="Remove photo"
          >
            ✕
          </button>
        </div>
      ) : (
        <label
          className="sidebar-photo-upload"
          onClick={e => { e.preventDefault(); inputRef.current?.click(); }}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          <span className="sidebar-photo-upload-btn">
            {uploading ? 'Uploading…' : '+ Add Photo'}
          </span>
        </label>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="u-hidden"
        disabled={uploading}
        onChange={e => { const f = e.target.files?.[0]; if (f) onFileSelect(f); }}
      />
    </section>
  );
}

export default SidebarPhotoCard;
